export const UPLOAD_QUERY_KEYS = {
  all: ['upload'] as const,
  credentials: (cId: number) => [...UPLOAD_QUERY_KEYS.all, 'credentials', cId] as const,
  ingestcfgs: (cId: number) => [...UPLOAD_QUERY_KEYS.all, 'ingestcfgs', cId] as const,
  jobs: (cId: number) => [...UPLOAD_QUERY_KEYS.all, 'jobs', cId] as const,
} as const;

/**
 * The backend is extension-agnostic (ffprobe/ffmpeg content-sniff is the real
 * gate; `Format.fromFileExt` maps unknown → "dat" without rejecting), so this
 * list is a UX filter only. It covers every extension observed in prod
 * transcode-job history (job.data srcFileFormat, 2026-01→08: all completed
 * successfully incl. mts 243/243, mxf 39/39, asf 10/10) plus same-demuxer
 * siblings (m2ts/m2t/ts = mts; 3gp/3g2 = mp4; vob = mpg; qt = mov). Do NOT
 * add audio-only extensions (mp3/wav/wma/m4a): ingest fails non-video jobs
 * ("Job type not implemented" / no-fps probe error).
 */
export const VIDEO_EXTENSIONS = [
  '.mp4', '.mov', '.qt', '.avi', '.mkv', '.webm', '.m4v', '.wmv', '.asf', '.flv',
  '.mpg', '.mpeg', '.vob', '.mts', '.m2ts', '.m2t', '.ts', '.mxf', '.3gp', '.3g2',
] as const;

/**
 * 16MB — part size is also the RETRY UNIT: a dropped connection re-sends up to
 * one part, so mobile links pay 16MB per drop (64MB here caused heavy re-send
 * loss for phone uploaders; legacy Fine Uploader used 12MB). Files >160GB get
 * ceil(size/10000) to stay under S3's 10k-part cap.
 */
export const MULTIPART_MIN_PART_SIZE = 16_777_216;

/** 6 hours — buffer before credential expiration to trigger refresh */
export const CREDENTIAL_EXPIRATION_BUFFER_SECS = 21_600;

/** 5s between backend progress reports */
export const PROGRESS_REPORT_THROTTLE_MS = 5_000;

/**
 * @uppy/aws-s3 `limit`: ONE shared request queue over EVERY S3 call the plugin
 * makes — part PUTs, signPart, create/complete. There is no separate per-file
 * part parallelism; this value IS the part parallelism. 6 matches the Uppy
 * default and the browser's per-host connection budget. Setting it to 1
 * (shipped 2026-02..07) serialized sign→PUT→sign→PUT with zero pipelining and
 * was the root cause of the "uploads slower than legacy" reports.
 */
export const UPLOAD_REQUEST_CONCURRENCY = 6;

/** 300GB — professional media: multi-hour 4K at high bitrate */
export const MAX_UPLOAD_SIZE_BYTES = 322_122_547_200;

/** 5s when active uploads exist */
export const JOB_POLL_ACTIVE_INTERVAL_MS = 5_000;

/** 5 min — mark upload as error if no progress change */
export const UPLOAD_STALE_TIMEOUT_MS = 5 * 60 * 1_000;

/**
 * Per-part retry schedule (~4 min total) — sized to ride out mobile network
 * blips (elevator, cell handoff) that a 2-min window didn't survive. Legacy
 * allowed 60 attempts @5s. NOTE: Uppy never retries S3 4xx responses
 * regardless of this schedule.
 */
export const UPLOAD_RETRY_DELAYS = [
  1_000, 2_000, 4_000, 8_000, 15_000, 30_000, 30_000, 30_000, 30_000, 30_000, 30_000, 30_000,
] as const;
